import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Interface pour le contexte d'authentification
export interface AuthContextType {
  user: any | null;
  login: (address: string) => Promise<void>;
  logout: () => Promise<void>;
  isLoading: boolean;
  loading: boolean; // Alias pour isLoading
  setUser: (user: any) => void; // Ajout de setUser
}

// Création du contexte avec une valeur par défaut
const AuthContext = createContext<AuthContextType>({
  user: null,
  login: async () => {},
  logout: async () => {},
  isLoading: true,
  loading: true,
  setUser: () => {}
});

// Hook pour utiliser le contexte d'authentification
export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

// Provider du contexte d'authentification
const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Effet pour vérifier l'état d'authentification au chargement
  useEffect(() => {
    // Simuler la vérification d'authentification
    const checkAuth = async () => {
      try {
        // Vérification de l'existence d'une session sauvegardée
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
          setUser(JSON.parse(savedUser));
        }
      } catch (error) {
        console.error("Erreur lors de la vérification d'authentification:", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Fonction de connexion
  const login = async (address: string) => {
    try {
      // Simuler une connexion
      const userInfo = {
        id: `user_${Date.now()}`,
        address,
        displayName: `User_${address.substring(0, 6)}`,
      };

      setUser(userInfo);
      localStorage.setItem('user', JSON.stringify(userInfo));
    } catch (error) {
      console.error('Erreur de connexion:', error);
      throw error;
    }
  };

  // Fonction de déconnexion
  const logout = async () => {
    try {
      // Simuler une déconnexion
      setUser(null);
      localStorage.removeItem('user');
    } catch (error) {
      console.error('Erreur de déconnexion:', error);
      throw error;
    }
  };

  const value = {
    user,
    login,
    logout,
    isLoading,
    loading: isLoading, // Alias pour compatibilité
    setUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
export { AuthProvider };